import { Key, useState } from 'react'
import { Tooltip } from 'antd'
import styled from 'styled-components'
import * as Icons from '@ant-design/icons'
import { colours } from '@qflow/theme'
import { Queries } from 'admin/lib/Api'

interface ChangeData {
  Action: string
  ChangeDtm: string
  ChangeType: string
  ChangedBy?: string
  NewValue: string
  OldValue: string
  FieldPart?: string
}

interface FieldDetailsProps {
  label: string
  changes: ChangeData[]
}

const FormattedChange = (change: ChangeData, index: Key, users) => {
  const ACTION_ICON_MAPPING = {
    QCR: { text: 'QCR', title: 'Set by QCR' },
    // DA
    EditByDA: { text: 'DA', title: 'Edited by DA' },
    Qupid: { text: 'QUP', title: 'Updated via Qupid by DA' },
    Requeue: { text: 'REQ', title: 'Requeued by DA' },
    AssignmentByDA: { text: 'ABD', title: 'Assigned by DA' },
    Split: { text: 'SPL', title: 'Split by DA' },
    Merge: { text: 'MGE', title: 'Merged by DA' },
    ModuleTransfer: { text: 'MTD', title: 'Module Transfer by DA' },
    ProjectTransfer: { text: 'PTD', title: 'Project Transfer by DA' },
    MassInternalStatusUpdate: {
      text: 'MSU',
      title: 'Mass Status Update by DA'
    },
    MassInternalDataUpdate: { text: 'MDU', title: 'Mass Data Update by DA' },
    // Client
    EditByClient: { text: 'CUS', title: 'Edited by Client' },
    TonnageImport: { text: 'CWI', title: 'Client Weight Import' },

    // System
    TestData: { text: 'STD', title: 'System Test Data' },
    Migration: { text: 'SMU', title: 'System Migration Update' },
    SmartWasteExport: { text: 'SWE', title: 'Smart Waste Export' },
    AutomaticDuplicateDetection: {
      text: 'SDD',
      title: 'System Duplicate Detection'
    },
    TransformationEngine: {
      text: 'STE',
      title: 'Updated by System Transformation Engine'
    }
  }

  const tooltipStyles = {
    borderRadius: '5px',
    padding: '11px 13px',
    color: colours.CORE_NAVY,
    boxShadow: '0px 2px 8px 0px #00000038'
  }

  const tooltipTitle = (change: ChangeData) => {
    let title = ACTION_ICON_MAPPING[change.Action]?.title || change.Action
    if (title.endsWith('DA') && change.ChangedBy && users.isSuccess) {
      const usersFilteredArray = users.data?.filter(
        user => user.userId === change.ChangedBy
      )
      if (usersFilteredArray[0]) {
        title = title.replace('DA', usersFilteredArray[0].displayName)
      }
    }
    return title
  }

  return (
    <div key={index}>
      <ChangeDetails>
        <Tooltip
          title={tooltipTitle(change)}
          styles={{ body: tooltipStyles }}
          color={colours.WHITE}
          placement="right"
        >
          <ActionIcon className="FieldDetails__ActionIcon">
            {ACTION_ICON_MAPPING[change.Action]?.text || 'OTH'}
          </ActionIcon>
        </Tooltip>
        <ActionText>
          {change.FieldPart ? <b>{change.FieldPart}: </b> : ''}
          {change.OldValue ? <span>{change.OldValue}</span> : <i>Empty</i>}{' '}
          <FromTo />{' '}
          {change.NewValue ? <span>{change.NewValue}</span> : <i>Empty</i>}
          <StyledDate>
            {new Date(change.ChangeDtm).toLocaleString([], {
              timeZoneName: 'short',
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric'
            })}
          </StyledDate>
        </ActionText>
      </ChangeDetails>
    </div>
  )
}

export const FieldDetails = ({ label, changes }: FieldDetailsProps) => {
  const [open, setOpen] = useState(false)
  const users = Queries.useQflowUsers()

  return (
    <StyledDetails className="FieldDetails__StyledDetails">
      <summary
        onClick={() => {
          setOpen(prev => !prev)
        }}
      >
        {open ? (
          <DownOutlined title="Click to collapse" />
        ) : (
          <RightOutlined title="Click to expand" />
        )}
        {label}
      </summary>
      {[...changes]
        .reverse()
        .map((change, index) => FormattedChange(change, index, users))}
    </StyledDetails>
  )
}

export const StyledDetails = styled.details`
  position: relative;
  padding: 0 12px;
  border-bottom: 1px solid ${colours.CLOUD_GREY};

  &:last-of-type {
    border-bottom: none;
  }

  & SUMMARY {
    height: 32px;
    justify-content: left;
    gap: 8px;
  }

  &[open] SUMMARY {
    color: ${colours.TEAL};
  }
`
export const RightOutlined = styled(Icons.RightOutlined)`
  font-size: 14px;
  color: inherit;
  padding-bottom: 1px;
`
export const DownOutlined = styled(Icons.DownOutlined)`
  font-size: 14px;
  color: ${colours.TEAL};
  padding-bottom: 1px;
`
export const ChangeDetails = styled.div`
  display: flex;
  gap: 8px;
`
export const ActionIcon = styled.div`
  padding: 3px 2px;
  border-radius: 5px;
  height: 22px;
  width: 30px;
  min-width: 30px;
  font-size: 10px;
  font-weight: 600;
  text-align: center;
`
export const ActionText = styled.div`
  padding: 0;
`
export const FromTo = styled(Icons.ArrowRightOutlined)`
  font-size: 12px;

  & SVG {
    margin-bottom: 1px;
  }
`
export const StyledDate = styled.div`
  font-size: 12px;
`
